import React, {useEffect, useState, useMemo} from "react"
import {useParams} from "react-router-dom";
import BreadCrumb from "../../../Common/BreadCrumb";
import {useTranslation} from "react-i18next";
import {Form, Container, Card, Row, Col} from "react-bootstrap";
import {mockScenarios} from "../../../data/scenarios";
import {getFieldLabel, getOperatorLabel, getValueLabel} from "../../../utils/labelUtils";
import {dashboardAPI} from "../../../services/api";

const ScenarioDetails = () => {
    const {id} = useParams();
    const {t} = useTranslation();
    const [scenario, setScenario] = useState<Scenario | null>(null);
    const [isLoading, setIsLoading] = useState(false);

    // Check if user is demo user
    const isDemoUser = useMemo(() => {
        const authUser = sessionStorage.getItem("authUser");
        if (!authUser) return false;
        try {
            const userData = JSON.parse(authUser);
            const token = userData.token || userData.access_token;
            return token?.startsWith('demo-token-') || userData.email === 'admin@demo.com.tr';
        } catch {
            return false;
        }
    }, []);

    useEffect(() => {
        if (!id) return;

        if (isDemoUser) {
            // Use mock data for demo users
            const foundScenario = mockScenarios.find((s) => s.id === id);
            setScenario(foundScenario || null);
            return;
        }

        // Fetch scenario from backend for real users
        const fetchScenario = async () => {
            setIsLoading(true);
            try {
                console.log('📋 Fetching scenario from backend:', id);
                const response: any = await dashboardAPI.getScenarioById(id);
                
                // Normalize response - handle both AxiosResponse and direct data
                const scenarioData = response?.data || response;
                
                if (!scenarioData) {
                    console.warn('⚠️ Scenario not found:', id);
                    setScenario(null);
                    return;
                }
                
                console.log('✅ Scenario received:', scenarioData);
                
                // Parse conditionTreeJson to extract conditions
                let conditions: ScenarioConditions[] = [];
                if (scenarioData.conditionTreeJson) {
                    try {
                        const conditionTree = JSON.parse(scenarioData.conditionTreeJson);
                        conditions = extractConditionsFromTree(conditionTree);
                    } catch (e) {
                        console.warn('⚠️ Failed to parse conditionTreeJson:', e);
                    }
                }
                
                // Format date (backend returns LocalDateTime, convert to string)
                const createdOn = scenarioData.createdOn 
                    ? (typeof scenarioData.createdOn === 'string' 
                        ? scenarioData.createdOn.split('T')[0] 
                        : new Date(scenarioData.createdOn).toISOString().split('T')[0])
                    : new Date().toISOString().split('T')[0];
                
                const mappedScenario: Scenario = {
                    id: scenarioData.id || id,
                    name: scenarioData.name || '',
                    active: scenarioData.active !== false,
                    createdOn: createdOn,
                    description: scenarioData.description || '',
                    conditions: conditions
                };
                
                setScenario(mappedScenario);
            } catch (error: any) {
                console.error('❌ Failed to fetch scenario:', error);
                setScenario(null);
            } finally {
                setIsLoading(false);
            }
        };

        fetchScenario();
    }, [id, isDemoUser]);

    // Helper function to extract conditions from condition tree
    const extractConditionsFromTree = (node: any): ScenarioConditions[] => {
        if (!node) return [];
        
        const conditions: ScenarioConditions[] = [];
        
        if (node.type === 'CONDITION' && node.condition) {
            // Single condition node
            conditions.push({
                field: node.condition.field || '',
                operator: node.condition.operator || '',
                value: node.condition.value || ''
            });
        } else if (node.conditions && Array.isArray(node.conditions)) {
            // Logical node (AND/OR) - recursively extract from children
            node.conditions.forEach((child: any) => {
                conditions.push(...extractConditionsFromTree(child));
            });
        }
        
        return conditions;
    };

    return (
        <React.Fragment>
            <div className="page-content">
                <Container fluid>
                    <BreadCrumb
                        items={[
                            {label: t('Dashboard.header'), path: "/"},
                            {label: t('ScenarioList.page_title'), path: "/scenarios"},
                            {label: id as string, path: "/scenarios"}
                        ]}
                    />

                    <Card>
                        <Card.Body>
                            <h4 className="mb-4">{t('ScenarioList.scenario_details')}</h4>
                            {isLoading && <div>Loading...</div>}
                            {!isLoading && !scenario && <div>Scenario not found</div>}
                            {
                                !isLoading && scenario && <Form>
                                    <Row className="mb-3">
                                        <Form.Group as={Col} md="6" controlId="scenarioId">
                                            <Form.Label>{t('ScenarioList.scenario_id')}</Form.Label>
                                            <Form.Control type="text" value={scenario.id} disabled/>
                                        </Form.Group>

                                        <Form.Group as={Col} md="6" controlId="createdOn">
                                            <Form.Label>{t('ScenarioList.created_on')}</Form.Label>
                                            <Form.Control type="date" value={scenario.createdOn} disabled/>
                                        </Form.Group>
                                    </Row>

                                    <Form.Group className="mb-3" controlId="scenarioName">
                                        <Form.Label>{t('ScenarioList.scenario_name')}</Form.Label>
                                        <Form.Control type="text" value={scenario.name} readOnly/>
                                    </Form.Group>

                                    <Form.Group className="mb-3" controlId="description">
                                        <Form.Label>{t('ScenarioList.scenario_description')}</Form.Label>
                                        <Form.Control
                                            as="textarea"
                                            rows={3}
                                            value={scenario.description}
                                        />
                                    </Form.Group>

                                    <Form.Group controlId="activeStatus" className="mb-3">
                                        <Form.Label>{t('ScenarioList.active')}</Form.Label>
                                        <div>
                                            <Form.Check
                                                type="checkbox"
                                                role="switch"
                                                id="SwitchCheck9"
                                                checked={scenario.active}/>
                                        </div>
                                    </Form.Group>
                                    <Form.Label>{t('ScenarioList.scenario_conditions')}</Form.Label>
                                    {scenario.conditions.map((cond, idx) => (
                                        <div>
                                            <Row className="mb-3" key={idx}>
                                                <Col md={4}>
                                                    <Form.Control value={getFieldLabel(t,cond.field)} disabled/>
                                                </Col>
                                                <Col md={4}>
                                                    <Form.Control value={getOperatorLabel(t, cond.operator)} disabled/>
                                                </Col>
                                                <Col md={4}>
                                                    <Form.Control value={getValueLabel(t, cond.value)} disabled/>
                                                </Col>
                                            </Row>
                                        </div>
                                    ))}
                                </Form>
                            }

                        </Card.Body>
                    </Card>
                </Container>
            </div>

        </React.Fragment>
    )

}
export default ScenarioDetails;